<template>
  <div class="input-textarea" :class="{ fill, difference, small }">
    <label v-if="label || forReport || forState">
      <span v-if="label" v-html="label" />
      <Icon v-if="forReport" name="fieldsSAV" />
      <Icon v-if="forState" name="fieldsState" />
      <Tooltip v-if="tooltip" icon="info" :content="tooltip" />
    </label>
    <div>
      <div class="icon" v-if="icon">
        <Icon :name="icon" />
      </div>
      <textarea
        ref="input"
        :id="iuid"
        :class="{ invalid }"
        :placeholder="placeholder"
        :value="value"
        :autofocus="autofocus"
        @focus="handleFocus($event)"
        @blur="handleBlur($event)"
        @input="handleInput($event)"
        :disabled="disabled"
      />
    </div>
    <span class="hint" v-if="hint" v-html="hint" />
    <template v-if="difference">
      <button class="adopt" @click.stop.prevent="handleAdopt(true)">{{ $t('review.diff.adopt') }}</button>
      <button class="adopt" @click.stop.prevent="handleAdopt(false)">{{ $t('review.diff.ignore') }}</button>
    </template>
  </div>
</template>

<script>
  import Icon from '@/components/ui/Icon';
  import Tooltip from '@/components/ui/Tooltip';

  let uid = 0;

  export default {
    name: 'TextareaInput',
    components: {
      Tooltip,
      Icon,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      value: {
        type: String,
        default: null,
      },
      label: {
        type: String,
        default: null,
      },
      tooltip: {
        type: String,
        default: null,
      },
      placeholder: {
        type: String,
        default: null,
      },
      hint: {
        type: String,
        default: null,
      },
      autofocus: {
        type: Boolean,
        default: false,
      },
      icon: {
        type: String,
        default: null,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      forState: {
        type: Boolean,
        default: false,
      },
      forReport: {
        type: Boolean,
        default: false,
      },
      vuelidate: {
        type: Object,
        default: null,
      },
      //----------------------------------
      // CSS Related Properties
      //----------------------------------
      fill: {
        type: Boolean,
        default: false,
      },
      difference: {
        type: Boolean,
        default: false,
      },
      small: {
        type: Boolean,
        default: false,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      uid += 1;
      return {
        iuid: `input-textarea-${uid}`,
        blurred: false,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {
      invalid() {
        const $vuelidate = this.vuelidate;
        return ($vuelidate && $vuelidate.$dirty && $vuelidate.$invalid);
      },
      dirty() {
        return this.value !== null;
      },
    },
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      value() {
        const $vuelidate = this.vuelidate;
        if ($vuelidate && $vuelidate.$dirty && this.blurred) {
          $vuelidate.$touch();
        }
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {

      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleFocus(evt) {
        this.$emit('focus', evt);
      },
      handleBlur(evt) {
        const $vuelidate = this.vuelidate;
        if ($vuelidate) {
          this.blurred = true;
          if (this.dirty) {
            $vuelidate.$touch();
          }
        }
        this.$emit('blur', evt);
      },
      handleInput(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        let value = this.$refs.input.value;
        value = /\S+/.test(value) ? value : null;
        this.$emit('update:value', value);
        this.$emit('input', value, evt);
      },
      handleAdopt(accept) {
        this.$emit('adopt', { accept, value: this.value });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/animations';
@import '~scss/mixins';
@import '~scss/variables';

.input-textarea {
  position: relative;

  & > label {
    display: block;
    @include font-body-medium;
    color: $color-black-solid-100;
    margin-bottom: 4px;

    & > svg {
      position: relative;
      top: 2px;
      width: 14px;
      height: 14px;
      margin-left: 4px;
    }

    & > .comp-tooltip {
      margin-left: 8px;
    }
  }

  & > span.hint {
    margin-top: 4px;
    @include font-body-small;
    color: $color-black-alpha-60;
  }

  & > button.adopt {
    display: inline-block;
    outline: 0;
    border: 0;
    cursor: pointer;
    background: transparent;
    text-decoration: underline;
    margin: 8px 0 0;
    @include font-body-small;
    color: $color-black-alpha-60;
  }

  & > div {
    display: inline-flex;

    textarea {
      width: 100%;
      padding: 11px;
      min-height: 90px;
      resize: vertical;
      height: 100%;
      border-radius: $default-border-radius;
      border: 1px solid $color-black-alpha-40;
      outline: 0;
      @include font-body-medium;
      $color: $color-black-solid-100;
      transition: border $default-transition;

      &::placeholder {
        @include font-body-medium;
        color: $color-black-alpha-60;
      }

      &:focus {
        border: 1px solid $color-blue-alpha-80;
      }

      &.invalid {
        border: 1px solid red;
      }
    }

    div.icon {
      position: relative;
      display: inline-flex;
      align-items: center;
      border: 1px solid $color-black-alpha-40;
      border-radius: $default-border-radius 0 0 $default-border-radius;
      border-right: 0;
      padding: 0 12px;

      & > svg {
        width: 18px;
        height: 18px;
      }

      & + input[type=text], input[type=password] {
        border-radius: 0 $default-border-radius $default-border-radius 0;
        border-left: 0;
        padding-left: 0;
      }
    }
  }
  &.fill > div {
    display: flex;
  }

  &.difference {
    & > div {
      textarea {
        box-shadow: 0 0 5px 3px rgba(214, 16, 16, 0.8);
      }
    }
  }

  &.small {
    & > div {
      textarea {
        min-height: 68px;
      }
    }
  }
}
</style>
